import React, { useCallback } from 'react';
import { Button, HStack, VStack } from '@chakra-ui/react';
import { useFilters } from '../../../../lib';
import { FilterComponent, LocationFilters, SearchFilter } from '.';

interface IRenderFiltersProps {
	isVisibleOnView?: boolean;
	isDefaultFiltersOpened?: boolean;
	setAreFiltersApplied?: React.Dispatch<React.SetStateAction<boolean>>;
	onClose?: () => void;
}

export const RenderFilters = ({
	isVisibleOnView,
	isDefaultFiltersOpened = true,
	setAreFiltersApplied,
	onClose,
}: IRenderFiltersProps) => {
	const { events, filters, searchTerm, defaultFilters, setFilters, setFilteredEvents, setSearchTerm } = useFilters();

	const handleApplyFilters = useCallback(() => {
		let filteredEvents = events;

		if (searchTerm) {
			const lowerCaseSearchTerm = searchTerm.toLowerCase();
			filteredEvents = filteredEvents.filter((event) => event.title.toLowerCase().includes(lowerCaseSearchTerm));
		}

		if (filters.selectedCountries.length > 0) {
			filteredEvents = filteredEvents.filter((event) => filters.selectedCountries.includes(event.country));
		}

		if (filters.selectedStates.length > 0) {
			filteredEvents = filteredEvents.filter((event) => filters.selectedStates.includes(event.state));
		}

		if (filters.selectedCities.length > 0) {
			filteredEvents = filteredEvents.filter((event) => filters.selectedCities.includes(event.city));
		}

		if (
			filters.selectedEventTypes.length !== defaultFilters.eventTypes.length &&
			filters.selectedEventTypes.length !== 0
		) {
			filteredEvents = filteredEvents.filter(
				(event) =>
					Array.isArray(event.eventType) &&
					event.eventType?.some((type) => filters.selectedEventTypes.includes(type))
			);
		}
		if (
			filters.selectedEventTags.length !== defaultFilters.eventTags.length &&
			filters.selectedEventTags.length !== 0
		) {
			filteredEvents = filteredEvents.filter(
				(event) =>
					Array.isArray(event.tags) && event.tags?.some((tag) => filters.selectedEventTags.includes(tag))
			);
		}
		if (
			filters.selectedEventLocations.length !== defaultFilters.eventLocations.length &&
			filters.selectedEventLocations.length !== 0
		) {
			filteredEvents = filteredEvents.filter((event) => filters.selectedEventLocations.includes(event.venue));
		}
		if (setAreFiltersApplied) {
			setAreFiltersApplied(true);
		}
		setFilteredEvents(filteredEvents);
		if (onClose) {
			onClose();
		}
	}, [events, searchTerm, filters, defaultFilters, setAreFiltersApplied, setFilteredEvents, onClose]);

	const handleResetFilters = useCallback(() => {
		setFilters({
			...filters,
			selectedEventTypes: [],
			selectedEventTags: [],
			selectedEventLocations: [],
			selectedCountries: [],
			selectedStates: [],
			selectedCities: [],
		});
		setFilteredEvents(events);
		setSearchTerm('');
		if (setAreFiltersApplied) {
			setAreFiltersApplied(false);
		}
		if (onClose) {
			onClose();
		}
	}, [events, filters, setFilters, setFilteredEvents, setSearchTerm, setAreFiltersApplied, onClose]);

	return (
		<VStack paddingBlock={isVisibleOnView ? 4 : 16} height='full' justifyContent='space-between'>
			<VStack w='full'>
				<SearchFilter />
				<LocationFilters />
				<FilterComponent
					filterType='EventLocations'
					defaultOptions={defaultFilters.eventLocations}
					selectedOptions={filters.selectedEventLocations}
					label='Venue'
					isDefaultOpened={isDefaultFiltersOpened}
				/>
				<FilterComponent
					filterType='EventTypes'
					defaultOptions={defaultFilters.eventTypes}
					selectedOptions={filters.selectedEventTypes}
					label='Category'
					isDefaultOpened={isDefaultFiltersOpened}
				/>
				<FilterComponent
					filterType='EventTags'
					defaultOptions={defaultFilters.eventTags}
					selectedOptions={filters.selectedEventTags}
					label='Tag'
					isDefaultOpened={isDefaultFiltersOpened}
				/>
			</VStack>
			<HStack w='full' alignItems='baseline' pb={6}>
				<Button
					colorScheme='blue'
					variant='solid'
					size='md'
					borderRadius='md'
					onClick={handleApplyFilters}
					w='full'
				>
					Apply Filters
				</Button>
				<Button
					ml={4}
					variant='outline'
					colorScheme='red'
					size='md'
					w='full'
					borderRadius='md'
					onClick={handleResetFilters}
				>
					Reset
				</Button>
			</HStack>
		</VStack>
	);
};
